import React from 'react';
import moment, { Moment } from 'moment-timezone';
import { DeepMap } from 'react-hook-form';
import {
  FormData,
  FormInput,
  TypedFormInputs,
  UseFormMethods,
  UseInputs,
  getUpdateInputValueFunction,
} from 'components/form';
import {
  JobsiteFormSubmission,
  EditFormData,
  FormContext,
  JobsiteFormSubmissionWorker,
  FormSubmissionTabDefinition,
  WatchedFieldConfig,
  CustomFormInputs,
  ContextDefaultSelectOptions,
  ContextSelectOptions,
  FormDynamicContext,
  JobsiteFormSubmissionContractor,
  CustomFormInputName,
} from 'containers/jobsiteFormSubmission/types';
import {
  createAvailableJobsiteWorkerOptions,
  formInputsAsArray,
  getFeaturesModule,
  useWatchedFields,
  useWatchedFormState,
} from 'containers/jobsiteFormSubmission/utils';
import { JobsiteUpdateFormSubmissionInput } from 'apollo/generated/client-operations';
import { ReplaceSelectOptionElement } from 'types';
import { AuthUser } from 'acl';
import {
  ensureNonUndefinedFields,
  evalJsCode,
  evalJsCodeAsync,
  isEmpty,
} from 'utils';
import { getDateTime } from 'utils/dates';
import {
  getDocumentsUpdateInput,
  getJobsiteWorkersUpdateInput,
  getJobsiteContractorsUpdateInput,
} from './forms';

export type GetFormInputsArgs = {
  jobsiteFormSubmission: JobsiteFormSubmission;
  defaultValues: EditFormData;
  evalContext: EvalContext<EditFormData | FormData>;
  setTabsDefinition: (tabs: FormSubmissionTabDefinition) => void;
};

export function getContextSelectOptions<T extends ContextDefaultSelectOptions>(
  options: T,
): ContextSelectOptions {
  return options as unknown as ContextSelectOptions;
}

function updateDateTimeZone<
  TFormData extends EditFormData | FormData = FormData,
>(options: {
  form: UseFormMethods<CustomFormInputs<TFormData>>;
  dateField: CustomFormInputName<TFormData>;
  timeZone: string | null;
}): void {
  const { timeZone, form } = options;
  type FormInputs = CustomFormInputs<TFormData>;
  const dateField = options.dateField as keyof FormInputs;
  const dateValue = form.getValues(dateField);
  const dateMoment = dateValue && moment(dateValue);
  if (dateMoment) {
    const newTimeZone = timeZone ?? moment.tz.guess();
    if (newTimeZone && newTimeZone !== dateMoment.tz()) {
      // Keep the local time (midnight) when changing the timezone.
      // When a new jobsite gets selected, the startAt time is set to midnight in the jobsite's timezone.
      // For example, if the user picks an EST jobsite then startAt is set to 00:00 in EST.
      const keepLocalTime = true;
      form.setValue(
        dateField,
        dateMoment.tz(
          newTimeZone,
          keepLocalTime,
        ) as FormInputs[keyof FormInputs],
      );
    }
  }
}

type GetEvalContextArgs<TFormData extends EditFormData | FormData> = {
  user: AuthUser;
  jobsiteFormSubmission: JobsiteFormSubmission;
  dependencies: Record<string, unknown>;
  edit?: CustomFormInputs<TFormData>;
  fn?: Partial<FormContext<TFormData>['fn']>;
  form?: UseFormMethods<CustomFormInputs<TFormData>>;
  options?: FormContext<TFormData>['options'];
  dynCtx?: FormDynamicContext<TFormData>;
};

export type EvalContext<TFormData extends EditFormData | FormData> = {
  ctx: FormContext<TFormData>;
};

export const getEvalContext = <TFormData extends EditFormData | FormData>(
  args: GetEvalContextArgs<TFormData>,
): EvalContext<TFormData> => {
  const {
    user,
    jobsiteFormSubmission,
    edit = {} as CustomFormInputs<TFormData>,
    fn,
    dependencies,
    form,
    options,
    dynCtx,
  } = args;
  const { jobsiteForm, jobsiteContractors, jobsiteWorkers, extraData } =
    jobsiteFormSubmission ?? {};
  const { getDateTimeUpdateInput, getUpdateInputValue } = fn ?? {};
  const features = getFeaturesModule(jobsiteForm?.jobsite.modules);
  const availableJobsiteWorkerOptions =
    options?.availableJobsiteWorkers ?? createAvailableJobsiteWorkerOptions();

  return {
    ctx: {
      isMobile: false,
      dependencies,
      fn: {
        ...fn,
        getDateTimeUpdateInput,
        getUpdateInputValue,
        updateDateTimeZone,
        ...dynCtx?.fn,
      },
      user,
      data: {
        ...jobsiteFormSubmission,
        extraData: extraData ?? {},
        jobsiteId: jobsiteForm?.jobsite.jobsiteId,
        contractorIds:
          jobsiteContractors?.edges.map(
            ({ node }) => node.jobsiteContractor.contractor.contractorId,
          ) ?? [],
        jobsiteWorkerIds:
          jobsiteWorkers?.edges.map(
            ({ node }) => node.jobsiteWorker.jobsiteWorkerId,
          ) ?? [],
        formSubmissionWorkersByAssociationType:
          jobsiteWorkers?.edges.reduce((result, { node }) => {
            if (!result[node.associationType])
              Object.assign(result, { [node.associationType]: [] });
            result[node.associationType].push(node);
            return result;
          }, {} as Record<string, JobsiteFormSubmissionWorker[]>) ?? {},
        formSubmissionContractorsByAssociationType:
          jobsiteContractors?.edges.reduce((result, { node }) => {
            if (!result[node.associationType])
              Object.assign(result, { [node.associationType]: [] });
            result[node.associationType].push(node);
            return result;
          }, {} as Record<string, JobsiteFormSubmissionContractor[]>) ?? {},
      },
      edit,
      form,
      features,
      options: getContextSelectOptions({
        ...options,
        availableJobsiteWorkers: availableJobsiteWorkerOptions,
        ...dynCtx?.options,
      }),
    },
  };
};

export const getFormInputsHook =
  (args: GetFormInputsArgs): UseInputs<CustomFormInputs<EditFormData>> =>
  (
    form: UseFormMethods<CustomFormInputs<EditFormData>>,
  ): FormInput<CustomFormInputs<EditFormData>>[] => {
    const { watch, getValues } = form;
    const {
      jobsiteFormSubmission,
      defaultValues,
      evalContext: baseEvalContext,
      setTabsDefinition,
    } = args;
    const {
      inputs: inputsExpression,
      tabs: tabsExpression,
      watchedFields: watchedFieldsConfig,
      watchedOptions: watchedOptionsConfig,
      watchedFormState: watchedFormStateConfig,
    } = jobsiteFormSubmission?.jobsiteForm.form.content.edit ?? {};

    const watchedFields = (
      watchedFieldsConfig as WatchedFieldConfig<
        CustomFormInputs<EditFormData>
      >[]
    ).map((item) => (typeof item === 'string' ? item : item.field));
    const watched = watch(watchedFields) as CustomFormInputs<EditFormData>;
    const state = { ...defaultValues, ...getValues(), ...watched };
    const { formState } = form;

    const evalContext = React.useMemo((): {
      ctx: FormContext<EditFormData>;
    } => {
      return (
        baseEvalContext && {
          ctx: { ...baseEvalContext.ctx, edit: state, form },
        }
      );
    }, [baseEvalContext, JSON.stringify(state), formState]);

    const { inputs, tabsBadges } = React.useMemo(() => {
      const computedInputs =
        evalContext &&
        evalJsCode<TypedFormInputs<EditFormData>>(
          inputsExpression,
          evalContext,
        );
      const computedTabsBadges =
        evalContext &&
        evalJsCode<FormSubmissionTabDefinition>(tabsExpression, evalContext);
      return {
        inputs: formInputsAsArray(computedInputs),
        tabsBadges: computedTabsBadges,
      };
    }, [inputsExpression, tabsExpression, evalContext]);

    React.useEffect(() => {
      if (!isEmpty(tabsBadges)) setTabsDefinition(tabsBadges);
    }, [JSON.stringify(tabsBadges)]);

    useWatchedFields(watchedFieldsConfig, watched, evalContext);

    const watchedOptions = evalContext?.ctx.options;
    useWatchedFields(watchedOptionsConfig, watchedOptions, evalContext);

    useWatchedFormState(watchedFormStateConfig, formState, evalContext);

    return inputs;
  };

type GetDefaultValuesArgs = {
  jobsiteFormSubmission: JobsiteFormSubmission;
  evalContext: EvalContext<EditFormData | FormData>;
};

export const getDefaultValues = (args: GetDefaultValuesArgs): EditFormData => {
  const { jobsiteFormSubmission, evalContext } = args;
  const { defaultValues: defaultValuesExpression } =
    jobsiteFormSubmission?.jobsiteForm.form.content.edit ?? {};

  return evalContext && evalJsCode(defaultValuesExpression, evalContext);
};

export type GetJobsiteFormSubmissionUpdateInputArgs = {
  jobsiteFormSubmission: JobsiteFormSubmission;
  evalContext: EvalContext<EditFormData | FormData>;
  updateInputsExpression: string;
  extraDataFields: string[];
  defaultValues: CustomFormInputs<EditFormData>;
  data: CustomFormInputs<EditFormData>;
  dirtyFields: DeepMap<CustomFormInputs<EditFormData>, true>;
};

export const getJobsiteFormSubmissionUpdateInput = async ({
  jobsiteFormSubmission: jfs,
  evalContext: baseEvalContext,
  updateInputsExpression,
  extraDataFields,
  defaultValues,
  data,
  dirtyFields,
}: GetJobsiteFormSubmissionUpdateInputArgs): Promise<JobsiteUpdateFormSubmissionInput> => {
  const getUpdateInputValue = getUpdateInputValueFunction(data, dirtyFields);

  /**
   * This function receives `date` and `time` as arguments and returns a date object composed based on the arguments.
   * The returned Date object will be calculated based on the `date` arguments as it follows:
   *  - if `date` is a field name, then the form field value will be used;
   *  - if `date` is a Date, then the passed value will be used.
   * @returns
   */
  const getDateTimeUpdateInput = (args: {
    date: string | Date | Moment;
    time: string;
    timeZone: string;
  }): Date => {
    const { date, time, timeZone } = args;
    const isDateAField = typeof date === 'string' && date in data;
    // get `updDate` only if a field name is specified through date parameter
    const updDate = isDateAField ? getUpdateInputValue(date) : undefined;
    const updTime = getUpdateInputValue(time) as string;
    return updDate || updTime
      ? getDateTime({
          date: updDate ?? moment(isDateAField ? data[date] : date),
          time: updTime ?? (time in data ? (data[time] as string) : time),
          timeZone,
        })
      : undefined;
  };

  const evalContext = {
    ctx: {
      ...baseEvalContext.ctx,
      edit: data,
      fn: { getDateTimeUpdateInput, getUpdateInputValue },
    },
  };
  const customUpdateInputs = await evalJsCodeAsync<
    Omit<JobsiteUpdateFormSubmissionInput, 'extraData'> &
      ReplaceSelectOptionElement<CustomFormInputs<EditFormData>>
  >(updateInputsExpression, evalContext);

  const extraDataInput = Object.fromEntries(
    extraDataFields.map((field) => [
      field,
      customUpdateInputs[field] ?? getUpdateInputValue(field),
    ]),
  );

  extraDataFields.forEach((field) => {
    delete customUpdateInputs[field];
  });

  const { form } = jfs?.jobsiteForm ?? {};

  const documentsUpdateInput = await getDocumentsUpdateInput({
    data,
    dirtyFields,
    defaultValues,
    form,
  });

  const getDateTimeDefaultInput = (
    fieldName: keyof Pick<
      JobsiteUpdateFormSubmissionInput,
      'startAt' | 'endAt'
    >,
  ): Date => {
    if (fieldName in customUpdateInputs) return undefined; // update is handled by updateInputs in form definition
    return getDateTimeUpdateInput({
      date: jfs.endAt ?? jfs.startAt,
      time: fieldName,
      timeZone: jfs.timeZone,
    });
  };

  return ensureNonUndefinedFields<JobsiteUpdateFormSubmissionInput>({
    id: jfs.id,
    extraData: extraDataInput,
    startAt: getDateTimeDefaultInput('startAt'),
    endAt: getDateTimeDefaultInput('endAt'),
    ...getJobsiteWorkersUpdateInput({ data, dirtyFields, defaultValues, form }),
    ...getJobsiteContractorsUpdateInput({
      data,
      dirtyFields,
      defaultValues,
      form,
    }),
    ...documentsUpdateInput,
    ...customUpdateInputs,
  });
};
